import type { Vec2 } from './Vec2';
import { vec2, dot, scale, sub, lengthSq } from './Vec2';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants';

export interface CollisionResult {
  normal: Vec2;  // outward normal at collision point
  depth: number; // penetration depth (positive = overlapping)
}

export interface Terrain {
  collide(center: Vec2, radius: number): CollisionResult | null;
  verticalWrap: boolean;
}

export interface BallState {
  pos: Vec2;
  vel: Vec2;
  atRest: boolean;
}

export const PHYSICS = {
  FIXED_DT: 1 / 240,
  GRAVITY: 500,
  AIR_DRAG: 0.1,
  RESTITUTION: 0.6,
  FRICTION: 0.4,
  BALL_RADIUS: 7,
  REST_SPEED: 2,
  REST_SPEED_BOUNCE: 20,
} as const;

export function createBall(x: number, y: number, vx: number, vy: number): BallState {
  return {
    pos: vec2(x, y),
    vel: vec2(vx, vy),
    atRest: false,
  };
}

export function wrapX(x: number): number {
  return ((x % GAME_WIDTH) + GAME_WIDTH) % GAME_WIDTH;
}

function wrapY(y: number): number {
  return ((y % GAME_HEIGHT) + GAME_HEIGHT) % GAME_HEIGHT;
}

export function step(ball: BallState, terrain: Terrain): void {
  if (ball.atRest) return;

  const dt = PHYSICS.FIXED_DT;

  // Gravity
  ball.vel.y += PHYSICS.GRAVITY * dt;

  // Air drag
  const dragFactor = 1 - PHYSICS.AIR_DRAG * dt;
  ball.vel.x *= dragFactor;
  ball.vel.y *= dragFactor;

  // Update position
  ball.pos.x += ball.vel.x * dt;
  ball.pos.y += ball.vel.y * dt;

  // Horizontal wrapping
  ball.pos.x = wrapX(ball.pos.x);

  // Vertical wrapping (cave mode only)
  if (terrain.verticalWrap) {
    ball.pos.y = wrapY(ball.pos.y);
  }

  // Terrain collision
  const collision = terrain.collide(ball.pos, PHYSICS.BALL_RADIUS);
  if (collision) {
    // Push ball out of terrain
    ball.pos.x += collision.normal.x * collision.depth;
    ball.pos.y += collision.normal.y * collision.depth;

    // Decompose velocity into normal and tangential components
    const velDotNormal = dot(ball.vel, collision.normal);
    const normalComponent = scale(collision.normal, velDotNormal);
    const tangentComponent = sub(ball.vel, normalComponent);

    if (Math.abs(velDotNormal) < PHYSICS.REST_SPEED_BOUNCE) {
      // Too slow to bounce — zero normal component
      ball.vel.x = tangentComponent.x;
      ball.vel.y = tangentComponent.y;
    } else {
      // Bounce: reflect and dampen normal component
      ball.vel.x = tangentComponent.x - normalComponent.x * PHYSICS.RESTITUTION;
      ball.vel.y = tangentComponent.y - normalComponent.y * PHYSICS.RESTITUTION;
    }

    // Apply friction to tangential component
    ball.vel.x -= tangentComponent.x * PHYSICS.FRICTION;
    ball.vel.y -= tangentComponent.y * PHYSICS.FRICTION;

    // Rest detection: on surface and slow enough
    if (lengthSq(ball.vel) < PHYSICS.REST_SPEED * PHYSICS.REST_SPEED) {
      ball.atRest = true;
      ball.vel.x = 0;
      ball.vel.y = 0;
    }
  }
}

export function advance(ball: BallState, terrain: Terrain, time: number): number {
  let remaining = time;
  while (remaining >= PHYSICS.FIXED_DT) {
    step(ball, terrain);
    remaining -= PHYSICS.FIXED_DT;
    if (ball.atRest) return 0;
  }
  return remaining;
}

const SIMULATE_SAMPLE_INTERVAL = 4;

export function simulate(
  ball: BallState,
  terrain: Terrain,
  maxSteps: number
): Vec2[] {
  // Clone ball state
  const sim: BallState = {
    pos: vec2(ball.pos.x, ball.pos.y),
    vel: vec2(ball.vel.x, ball.vel.y),
    atRest: ball.atRest,
  };

  const trajectory: Vec2[] = [vec2(sim.pos.x, sim.pos.y)];

  for (let i = 0; i < maxSteps; i++) {
    step(sim, terrain);
    if (i % SIMULATE_SAMPLE_INTERVAL === 0) {
      trajectory.push(vec2(sim.pos.x, sim.pos.y));
    }
    if (sim.atRest) {
      trajectory.push(vec2(sim.pos.x, sim.pos.y));
      break;
    }
  }

  return trajectory;
}
